import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import CatalogConcierge from '../components/CatalogConcierge';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import {
  Search, MapPin, SlidersHorizontal, Loader2, Award,
  Users, Baby, Heart, ArrowRight, Sparkles, ChevronDown,
  X, MessageCircle, Phone, Navigation, Bell, Flame, Eye,
  ArrowDownWideNarrow, Check
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { API } from '@/utils/api';
import SearchBar from '@/components/SearchBar';
import OrganizationBadge from '@/components/OrganizationBadge';
import TopNavBar from '@/components/TopNavBar';
import MarketplaceFooter from '@/components/MarketplaceFooter';
import ViewCounter from '@/components/ViewCounter';
import { PLACEHOLDER_IMAGE, getImageUrl, getPlaceholderByType } from '@/utils/imageUrl';
import { trackEvent, toggleFavorite, getFavorites } from '@/utils/marketplaceEvents';
import SaveSearchModal from '@/components/SaveSearchModal';
import QuickLeadModal from '@/components/QuickLeadModal';
import LoginPromptModal, { shouldShowLoginPrompt } from '@/components/LoginPromptModal';
import KennelAvatar from '@/components/KennelAvatar';
import useRecentlyViewed from '@/hooks/useRecentlyViewed';
import { toast } from 'sonner';

const TYPE_TABS = [
  { key: 'all', label: 'Wszystkie', icon: Sparkles, countKey: 'all', color: 'white' },
  { key: 'hodowle', label: 'Hodowle', icon: Users, countKey: 'hodowle', color: 'blue' },
  { key: 'mioty', label: 'Szczenięta', icon: Baby, countKey: 'mioty', color: 'emerald' },
  { key: 'reproduktory', label: 'Reproduktory', icon: Heart, countKey: 'reproduktory', color: 'purple' },
];

const TYPE_LABELS = {
  all: 'Wszystkie',
  hodowle: 'Hodowle',
  mioty: 'Szczenięta',
  reproduktory: 'Reproduktory',
};

const TYPE_EMPTY_NOUN = {
  all: 'ofert',
  hodowle: 'hodowli',
  mioty: 'szczeniąt',
  reproduktory: 'reproduktorów',
};

const SORT_OPTIONS_BASE = [
  { key: 'newest', label: 'Najnowsze' },
  { key: 'views_desc', label: 'Najwięcej wyświetleń' },
  { key: 'distance', label: 'Blisko mnie', requiresGeo: true },
];
const SORT_OPTIONS_WITH_PRICE = [
  ...SORT_OPTIONS_BASE,
  { key: 'price_asc', label: 'Cena rosnąco' },
  { key: 'price_desc', label: 'Cena malejąco' },
];

const SORT_OPTIONS_HODOWLE = [
  ...SORT_OPTIONS_BASE,
  { key: 'litters', label: 'Najwięcej miotów' },
];

const FILTER_OPTIONS = [
  { key: 'all', label: 'Wszystkie', param: { rodowod: 'all', verified: false, status: null } },
  { key: 'available', label: 'Dostępne teraz', param: { status: 'available' } },
  { key: 'verified', label: 'Zweryfikowane', param: { verified: true } },
];

// Lokalny AddDropdown zastąpiony przez /components/AddDropdown.jsx (reusable)

function StatusBadge({ status }) {
  if (!status) return null;
  const map = {
    available: { label: 'Dostępne', cls: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40' },
    planned: { label: 'Planowane', cls: 'bg-blue-500/20 text-blue-300 border-blue-500/40' },
    reserved: { label: 'Rezerwacje', cls: 'bg-amber-500/20 text-amber-300 border-amber-500/40' },
    sold: { label: 'Sprzedane', cls: 'bg-gray-500/20 text-gray-400 border-gray-500/40' },
  };
  const v = map[status];
  if (!v) return null;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] font-medium ${v.cls}`}>
      {v.label}
    </span>
  );
}

function TypeBadge({ type }) {
  const map = {
    hodowla: { label: 'Hodowla', cls: 'bg-blue-500/15 text-blue-300 border-blue-500/30' },
    miot: { label: 'Miot', cls: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' },
    reproduktor: { label: 'Reproduktor', cls: 'bg-purple-500/15 text-purple-300 border-purple-500/30' },
  };
  const v = map[type];
  if (!v) return null;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] font-medium ${v.cls}`}>
      {v.label}
    </span>
  );
}

function formatAddedAgo(iso) {
  if (!iso) return null;
  try {
    const then = new Date(iso).getTime();
    if (!then) return null;
    const diffMs = Date.now() - then;
    const days = Math.floor(diffMs / 86400000);
    if (days <= 0) return 'dzisiaj';
    if (days === 1) return 'wczoraj';
    if (days < 7) return `${days} dni temu`;
    if (days < 30) return `${Math.floor(days / 7)} tyg. temu`;
    if (days < 365) return `${Math.floor(days / 30)} mies. temu`;
    return `${Math.floor(days / 365)} lat temu`;
  } catch { return null; }
}

function getPrimaryCtaLabel(item) {
  if (item.type === 'hodowla') return 'Zobacz hodowlę';
  if (item.type === 'reproduktor') return 'Zapytaj o krycie';
  return 'Zapytaj o szczenięta';
}

function getConversionStatus(item) {
  const leads24 = Number(item.leads_last_24h || 0);
  const avail = Number(item.available_count || 0);
  if (leads24 > 5) {
    return { label: 'Duże zainteresowanie', cls: 'text-orange-300', dot: 'bg-orange-400', testid: 'status-hot' };
  }
  if (avail > 0 && avail <= 3) {
    return { label: `Zostały ${avail}`, cls: 'text-amber-300', dot: 'bg-amber-400', testid: 'status-low' };
  }
  if (item.status === 'reserved') {
    return { label: 'Rezerwacja możliwa po kontakcie', cls: 'text-amber-200', dot: 'bg-amber-300', testid: 'status-reserved' };
  }
  if (item.status === 'sold') {
    return { label: 'Sprzedane', cls: 'text-gray-400', dot: 'bg-gray-500', testid: 'status-sold' };
  }
  if (item.status === 'planned') {
    return { label: 'Planowane', cls: 'text-blue-300', dot: 'bg-blue-400', testid: 'status-planned' };
  }
  return { label: 'Dostępne', cls: 'text-emerald-300', dot: 'bg-emerald-400', testid: 'status-available' };
}

function ResultCard({ item, isFavorite, onFavoriteToggle, onLeadClick, onView }) {
  const navigate = useNavigate();
  const isLitter = item.type === 'miot';
  const isKennel = item.type === 'hodowla';
  const typeLabel = isKennel ? 'Hodowla' : isLitter ? 'Szczenięta' : 'Reproduktor';
  const addedAgo = formatAddedAgo(item.created_at);
  const statusInfo = getConversionStatus(item);
  const primaryCta = getPrimaryCtaLabel(item);
  const isPrimaryViewType = isKennel; // hodowla → "Zobacz hodowlę" (navigate, no lead modal)

  const handleCardClick = () => {
    trackEvent('view_click', { type: item.type, slug: item.slug });
    onView?.(item);
    if (item.url) navigate(item.url);
  };

  const handleLeadCtaClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isPrimaryViewType) {
      // Hodowla — primary CTA "Zobacz hodowlę" jest navigation, nie lead modal
      trackEvent('view_click', { type: item.type, slug: item.slug });
      onView?.(item);
      if (item.url) navigate(item.url);
      return;
    }
    trackEvent('lead_cta_click', { type: item.type, slug: item.slug });
    onLeadClick?.(item);
  };

  const handleWhatsAppClick = (e) => {
    e.stopPropagation();
    if (!item.whatsapp_url) {
      e.preventDefault();
      return;
    }
    trackEvent('whatsapp_click', { type: item.type, slug: item.slug });
  };

  const handleTelClick = (e) => {
    e.stopPropagation();
    if (!item.contact_phone) {
      e.preventDefault();
      return;
    }
    trackEvent('tel_click', { type: item.type, slug: item.slug });
  };

  const handleFavoriteClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onFavoriteToggle?.(item);
  };

  return (
    <article
      data-testid={`catalog-card-${item.type}-${item.slug}`}
      onClick={handleCardClick}
      className="group rounded-2xl bg-white/5 border border-white/10 hover:border-orange-400/40 hover:bg-white/[0.07] hover:shadow-xl hover:shadow-black/40 overflow-hidden transition-all cursor-pointer"
    >
      <div className="grid md:grid-cols-[140px_1fr_240px] gap-0">
        {/* COL 1 — image (140x140 desktop). Real image = object-cover; fallback = deterministyczny avatar z inicjałami */}
        <div className="relative h-[180px] md:h-[140px] md:w-[140px] m-2 md:m-3 rounded-xl bg-[#080f1f] overflow-hidden">
          {item.image ? (
            <img
              src={getImageUrl(item.image)}
              alt={item.title}
              className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
              loading="lazy"
            />
          ) : (
            <KennelAvatar
              name={item.title}
              seed={item.slug || item.id}
              type={item.type}
              className="group-hover:scale-[1.03] transition-transform duration-500"
            />
          )}
          {item.sponsored && (
            <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-yellow-400 text-black text-[10px] font-bold" data-testid="badge-sponsored">
              Sponsorowane
            </span>
          )}
          {Number(item.photos_count || 0) > 1 && (
            <div className="absolute bottom-2 right-2 px-2 py-0.5 rounded-full bg-black/70 text-white text-[10px] font-medium inline-flex items-center gap-1">
              📸 {item.photos_count}
            </div>
          )}
          {isLitter && addedAgo && ['dzisiaj', 'wczoraj'].includes(addedAgo) && (
            <span className="absolute top-2 right-2 px-2 py-0.5 rounded-full bg-emerald-400 text-black text-[10px] font-bold">Nowe</span>
          )}
        </div>

        {/* COL 2 — content */}
        <div className="p-4 md:p-5 min-w-0">
          <div className="flex flex-wrap items-center gap-1.5 mb-2">
            <span className="text-[11px] px-2 py-0.5 rounded-full bg-white/10 text-gray-200 border border-white/10">{typeLabel}</span>
            <OrganizationBadge code={item.organization_code} />
            {item.subtitle && (
              <span className="text-[11px] px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-gray-300 inline-flex items-center gap-1">
                <MapPin className="w-3 h-3" /> {item.subtitle}
              </span>
            )}
            {item.verified && (
              <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 inline-flex items-center gap-1">
                <Award className="w-3 h-3" /> Zweryfikowane
              </span>
            )}
          </div>

          <h3 className="text-lg md:text-xl font-semibold text-white leading-tight line-clamp-2">
            {item.title}
          </h3>

          {/* Status konwersyjny — kropka + tekst dla czytelności (P2) */}
          <div className={`mt-2 text-sm font-medium inline-flex items-center gap-1.5 ${statusInfo.cls}`} data-testid={`card-status-${statusInfo.testid}`}>
            <span className={`w-2 h-2 rounded-full ${statusInfo.dot}`} aria-hidden />
            {statusInfo.label}
          </div>

          {item.kennel_name && !isKennel && (
            <div className="mt-1.5 text-xs text-white/45">Hodowla: {item.kennel_name}</div>
          )}

          {isLitter && (
            <div className="mt-2 text-xs text-gray-400 flex flex-wrap items-center gap-x-2 gap-y-0.5">
              {item.puppies_count > 0 && (
                <span className="inline-flex items-center gap-1">
                  <Baby className="w-3 h-3" />
                  {item.puppies_count} szczeniąt
                </span>
              )}
              {item.sex_label && <span className="text-gray-300">{item.sex_label}</span>}
              {item.breed && <span>{item.breed}</span>}
            </div>
          )}

          {(addedAgo || true) && (
            <div className="mt-2 text-[11px] text-gray-500 flex flex-wrap items-center gap-x-3 gap-y-0.5">
              {addedAgo && <span>Dodano {addedAgo}</span>}
              <ViewCounter
                totalViews={Number(item.views_total) || 0}
                slug={item.slug}
                variant="card"
              />
            </div>
          )}
        </div>

        {/* COL 3 — actions (single primary CTA + favorite + tel + WA) */}
        <div className="p-4 md:p-5 flex md:flex-col items-stretch justify-between gap-3 border-t md:border-t-0 md:border-l border-white/5 bg-black/10">
          {(isLitter || item.type === 'reproduktor') && (
            <div className="md:text-right" data-testid={`card-price-${item.slug}`}>
              <div className="text-xs text-white/45">Cena</div>
              <div className="text-base font-bold text-white">
                {item.price_from ? `od ${item.price_from.toLocaleString('pl-PL')} zł` : 'na żądanie'}
              </div>
            </div>
          )}

          <div className="flex flex-col gap-2 w-full">
            {/* JEDEN główny CTA */}
            <Button
              onClick={handleLeadCtaClick}
              data-testid={`catalog-cta-primary-${item.slug}`}
              className={`w-full rounded-xl font-semibold h-10 text-sm shadow-lg ${
                isPrimaryViewType
                  ? 'bg-white text-black hover:bg-white/90'
                  : 'bg-orange-500 hover:bg-orange-400 text-black shadow-orange-500/20'
              }`}
            >
              {!isPrimaryViewType && <Flame className="w-4 h-4 mr-1.5" />}
              {primaryCta}
            </Button>
            <div className="text-[11px] text-white/45 text-center" data-testid={`card-microcopy-${item.slug}`}>
              Odpowiada zwykle w kilka godzin
            </div>

            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleFavoriteClick}
                data-testid={`card-favorite-${item.slug}`}
                aria-label={isFavorite ? 'Usuń z obserwowanych' : 'Dodaj do obserwowanych'}
                className={`inline-flex items-center justify-center rounded-lg border h-9 w-10 shrink-0 transition ${
                  isFavorite
                    ? 'border-pink-400 bg-pink-500/15 text-pink-300'
                    : 'border-white/15 bg-white/[0.03] text-white/60 hover:border-pink-400/60 hover:text-pink-300'
                }`}
              >
                <Heart className={`w-4 h-4 ${isFavorite ? 'fill-current' : ''}`} />
              </button>

              {item.contact_phone && (
                <a
                  href={`tel:${item.contact_phone}`}
                  onClick={handleTelClick}
                  data-testid={`catalog-cta-tel-${item.slug}`}
                  aria-label={`Zadzwoń ${item.contact_phone}`}
                  className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg border border-sky-500/40 bg-sky-500/10 text-sky-300 h-9 text-xs font-semibold hover:bg-sky-500/20 transition"
                >
                  <Phone className="w-4 h-4" />
                  <span className="hidden sm:inline">Zadzwoń</span>
                </a>
              )}

              {item.whatsapp_url && (
                <a
                  href={item.whatsapp_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={handleWhatsAppClick}
                  data-testid={`catalog-cta-whatsapp-${item.slug}`}
                  aria-label="Napisz na WhatsApp"
                  className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 h-9 text-xs font-semibold hover:bg-emerald-500/20 transition"
                >
                  <MessageCircle className="w-4 h-4" />
                  <span className="hidden sm:inline">WhatsApp</span>
                </a>
              )}
            </div>

            {/* Claim hint w stopce karty (tylko hodowla bez właściciela) */}
            {isKennel && item.is_orphan && (
              <Link
                to={`${item.url || '#'}?claim=1`}
                onClick={(e) => e.stopPropagation()}
                data-testid={`card-claim-hint-${item.slug}`}
                className="block text-[11px] text-orange-300/70 hover:text-orange-200 underline underline-offset-2 text-center mt-1"
              >
                To Twoja hodowla? Przejmij profil
              </Link>
            )}
          </div>
        </div>
      </div>
    </article>
  );
}

function SponsoredStrip({ sponsored }) {
  if (!sponsored?.length) return null;
  return (
    <section className="mb-6" data-testid="sponsored-strip">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="w-4 h-4 text-yellow-400" />
        <span className="text-xs uppercase tracking-[0.18em] text-yellow-400 font-semibold">Sponsorowane</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {sponsored.slice(0, 3).map(item => (
          <Link
            key={`${item.type}-${item.slug}`}
            to={item.url}
            className="group relative rounded-2xl overflow-hidden border border-yellow-500/30 bg-[#10182B]/80 hover:border-yellow-500/60 transition-all"
            data-testid={`sponsored-card-${item.type}-${item.slug}`}
          >
            <div className="relative aspect-[16/9] bg-[#080f1f]">
              <img src={getImageUrl(item.image) || PLACEHOLDER_IMAGE} alt={item.title} className="w-full h-full object-cover" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
              <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-yellow-400 text-black text-[10px] font-bold">Sponsorowane</span>
              <div className="absolute bottom-0 left-0 right-0 p-3">
                <div className="flex flex-wrap gap-1.5 mb-1.5">
                  <TypeBadge type={item.type} />
                  <OrganizationBadge code={item.organization_code} />
                </div>
                <p className="text-white font-semibold text-base leading-tight line-clamp-1">{item.title}</p>
                <p className="text-gray-300 text-xs mt-0.5 flex items-center gap-1"><MapPin className="w-3 h-3" /> {item.subtitle}</p>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

function SortDropdown({ value, options, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, []);
  const current = options.find(o => o.key === value) || options[0];
  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        data-testid="catalog-sort-trigger"
        className="inline-flex items-center gap-2 rounded-xl bg-white/[0.06] border border-white/10 px-3.5 h-9 text-sm text-white hover:bg-white/10 focus:outline-none focus:border-white/30 transition"
      >
        <ArrowDownWideNarrow className="w-3.5 h-3.5 text-white/50" />
        <span className="text-white/75">Sortuj:</span>
        <span className="font-semibold">{current.label}</span>
        <ChevronDown className={`w-4 h-4 text-white/50 transition ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div
          className="absolute right-0 md:right-0 top-11 z-50 min-w-[220px] rounded-2xl bg-[#0d1530] border border-white/10 shadow-2xl overflow-hidden"
          data-testid="catalog-sort-dropdown"
        >
          {options.map(o => (
            <button
              key={o.key}
              type="button"
              onClick={() => { onChange(o.key); setOpen(false); }}
              data-testid={`catalog-sort-option-${o.key}`}
              className={`w-full text-left px-4 py-2.5 text-sm flex items-center justify-between gap-3 hover:bg-white/5 transition ${
                value === o.key ? 'bg-white/5 text-white' : 'text-white/80'
              }`}
            >
              <span>{o.label}</span>
              {value === o.key && <Check className="w-4 h-4 text-emerald-400" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function KatalogPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [stats, setStats] = useState(null);
  const [favorites, setFavorites] = useState(new Set()); // entity_id set
  const [geoState, setGeoState] = useState({ status: 'idle', error: null }); // idle|loading|granted|denied
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [leadModalItem, setLeadModalItem] = useState(null);
  const [loginPromptCtx, setLoginPromptCtx] = useState(null); // 'favorite' | 'save_search' | null
  const [popularBreeds, setPopularBreeds] = useState([]);
  const [popularCities, setPopularCities] = useState([]);
  const [accumulated, setAccumulated] = useState([]); // wyniki sklejone z kolejnych stron („Pokaż więcej")
  const { recent, addRecent, clearRecent } = useRecentlyViewed();

  // Filter state (synced with URL)
  const type = searchParams.get('type') || 'all';
  const rasa = searchParams.get('rasa') || '';
  const miasto = searchParams.get('miasto') || '';
  const wojewodztwo = searchParams.get('wojewodztwo') || '';
  const rodowod = searchParams.get('rodowod') || 'all';
  const sort = searchParams.get('sort') || 'newest';
  const status = searchParams.get('status') || '';
  const verified = searchParams.get('verified') === 'true';
  const lat = searchParams.get('lat') ? parseFloat(searchParams.get('lat')) : null;
  const lng = searchParams.get('lng') ? parseFloat(searchParams.get('lng')) : null;
  const page = parseInt(searchParams.get('page') || '1', 10);

  const activeFilterKey = useMemo(() => {
    if (verified) return 'verified';
    if (status === 'available') return 'available';
    if (rodowod === 'fci') return 'fci';
    if (rodowod === 'other') return 'other';
    return 'all';
  }, [rodowod, status, verified]);

  // Normalize: map type param (from /szukaj or SearchBar) to tab keys
  const normalizedType = (() => {
    if (type === 'szczenieta' || type === 'mioty' || type === 'puppies') return 'mioty';
    if (type === 'hodowle' || type === 'kennels') return 'hodowle';
    if (type === 'reproduktory' || type === 'reproduktora' || type === 'studs') return 'reproduktory';
    return 'all';
  })();
  const emptyStateNoun = TYPE_EMPTY_NOUN[normalizedType] || 'ofert';

  // Active sort options based on tab (price hidden for hodowle, litters only for hodowle/all)
  const sortOptions = useMemo(() => {
    if (normalizedType === 'hodowle') return SORT_OPTIONS_HODOWLE;
    if (normalizedType === 'mioty' || normalizedType === 'reproduktory') return SORT_OPTIONS_WITH_PRICE;
    return [...SORT_OPTIONS_BASE, { key: 'price_asc', label: 'Cena rosnąco' }, { key: 'price_desc', label: 'Cena malejąco' }];
  }, [normalizedType]);

  // Fetch with AbortController for cleanup — akumulacja wyników przy „Pokaż więcej"
  useEffect(() => {
    const params = {
      type: normalizedType,
      rasa, miasto, wojewodztwo, rodowod, sort, verified_only: verified, page, per_page: 15,
    };
    if (status) params.status = status;
    if (sort === 'distance' && lat != null && lng != null) {
      params.lat = lat;
      params.lng = lng;
    }

    const ctrl = new AbortController();
    setLoading(true);
    axios.get(`${API}/public/catalog`, { params, signal: ctrl.signal })
      .then(res => {
        setData(res.data);
        const newResults = res.data?.results || [];
        setAccumulated(prev => (page > 1 ? [...prev, ...newResults] : newResults));
      })
      .catch((err) => {
        if (axios.isCancel?.(err) || err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;
        setData({ results: [], sponsored: [], counts: { all: 0, hodowle: 0, mioty: 0, reproduktory: 0 }, total: 0 });
        if (page === 1) setAccumulated([]);
      })
      .finally(() => {
        if (!ctrl.signal.aborted) setLoading(false);
      });

    return () => ctrl.abort();
  }, [normalizedType, rasa, miasto, wojewodztwo, rodowod, sort, status, verified, page, lat, lng]);

  useEffect(() => {
    axios.get(`${API}/public/catalog/stats`).then(res => setStats(res.data)).catch(() => {});
  }, []);

  // Top rasy (z realnymi ogłoszeniami) — raz przy mount
  useEffect(() => {
    axios.get(`${API}/public/rasy`)
      .then(res => {
        const all = res.data.breeds || [];
        setPopularBreeds(all.filter(b => b.has_content).slice(0, 8));
      })
      .catch(() => {});
  }, []);

  // Top miasta — raz przy mount (cała Polska)
  useEffect(() => {
    axios.get(`${API}/public/cities`, { params: { limit: 8 } })
      .then(res => setPopularCities(res.data.items || []))
      .catch(() => {});
  }, []);

  // Load favorites once
  useEffect(() => {
    getFavorites().then(items => {
      setFavorites(new Set(items.map(i => i.entity_id)));
    });
  }, []);

  const updateParams = useCallback((patch) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(patch).forEach(([k, v]) => {
      if (v === null || v === '' || v === false || v === undefined) next.delete(k);
      else next.set(k, String(v));
    });
    if (!('page' in patch)) next.delete('page');
    setSearchParams(next);
  }, [searchParams, setSearchParams]);

  const setTab = (tabKey) => updateParams({ type: tabKey === 'all' ? null : tabKey });

  const setSort = (s) => {
    if (s === 'distance') {
      // Trigger geolocation
      if (!navigator.geolocation) {
        setGeoState({ status: 'denied', error: 'Twoja przeglądarka nie wspiera geolokalizacji' });
        return;
      }
      setGeoState({ status: 'loading', error: null });
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setGeoState({ status: 'granted', error: null });
          updateParams({ sort: 'distance', lat: pos.coords.latitude.toFixed(5), lng: pos.coords.longitude.toFixed(5) });
        },
        (err) => {
          setGeoState({ status: 'denied', error: err.message || 'Brak zgody na lokalizację' });
        },
        { timeout: 8000, maximumAge: 600000 }
      );
      return;
    }
    updateParams({ sort: s, lat: null, lng: null });
  };

  const setFilter = (filterKey) => {
    const f = FILTER_OPTIONS.find(x => x.key === filterKey);
    if (!f) return;
    const patch = {};
    ['rodowod', 'status', 'verified'].forEach(k => { patch[k] = null; });
    if (f.param.rodowod) patch.rodowod = f.param.rodowod;
    if (f.param.status) patch.status = f.param.status;
    if (f.param.verified) patch.verified = true;
    updateParams(patch);
  };

  // Region change → reset city (per spec)
  const setVoivodeship = (v) => updateParams({ wojewodztwo: v || null, miasto: null });

  const handleSearch = (filters) => {
    const newType = filters.type === 'szczenieta' ? 'mioty' : filters.type;
    // If voivodeship changed, reset city
    const cityToSet = (filters.voivodeship && filters.voivodeship !== wojewodztwo) ? null : filters.city;
    updateParams({
      type: newType,
      rasa: filters.breed,
      miasto: cityToSet,
      wojewodztwo: filters.voivodeship,
    });
  };

  const handleFavoriteToggle = async (item) => {
    const next = new Set(favorites);
    const wasFavorite = next.has(item.id);
    if (wasFavorite) next.delete(item.id);
    else next.add(item.id);
    setFavorites(next);
    const result = await toggleFavorite(item.type, item.id);
    if (result !== !wasFavorite) {
      // Sync with backend if mismatch (use `next` not stale `favorites`)
      const corrected = new Set(next);
      if (result) corrected.add(item.id);
      else corrected.delete(item.id);
      setFavorites(corrected);
    }
    if (result) {
      toast.success('❤️ Zapisano w obserwowanych', {
        description: 'Wszystkie ulubione w jednym miejscu',
        action: {
          label: 'Zobacz',
          onClick: () => navigate('/moje/obserwowane'),
        },
      });
      // Retencja: po pierwszym zapisie pokaż delikatny prompt logowania (anonimowi userzy)
      if (shouldShowLoginPrompt()) {
        setTimeout(() => setLoginPromptCtx('favorite'), 1100);
      }
    } else {
      toast('Usunięto z obserwowanych');
    }
  };

  const counts = data?.counts || { all: 0, hodowle: 0, mioty: 0, reproduktory: 0 };
  const total = data?.total || 0;
  const sponsoredCount = data?.sponsored?.length || 0;
  // Łączna liczba realnie pokazanych ofert = organiczne (total) + sponsorowane na górze.
  // Zapewnia spójność z licznikiem w hero (stats) i eliminuje rozjazd „655 vs 654".
  const displayTotal = total + sponsoredCount;
  const totalPages = Math.max(1, Math.ceil(total / 20));

  return (
    <>
      <Helmet>
        <title>{`Katalog ApisAI — ${rasa ? `${rasa}, ` : ''}${miasto || 'cała Polska'} | Hodowle, mioty, reproduktory`}</title>
        <meta name="description" content={`Znajdź ${rasa ? rasa + ', ' : ''}hodowle, szczenięta i reproduktory w Polsce. ${stats?.kennels || ''} zarejestrowanych hodowli, ${stats?.litters || ''} miotów, ${stats?.breeds || ''} ras.`} />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-[#0a0f1e] via-[#0e1628] to-[#0a0f1e] text-white">
        {/* TOP NAV (marketplace primary — v2 P0 lock) */}
        <TopNavBar />

        {/* HERO + SEARCH (przywrócona wersja z eyebrow + subtitle + stats) */}
        <section className="pt-8 pb-6">
          <div className="max-w-6xl mx-auto px-4 md:px-6">
            <div className="text-center mb-7">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-gray-300 text-xs mb-4">
                <Sparkles className="w-3.5 h-3.5 text-yellow-400" />
                Wyszukiwarka hodowli, szczeniąt i reproduktorów
              </div>
              <h1 className="hidden sm:block text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight mb-4" data-testid="katalog-title">
                Znajdź szczenięta, hodowlę lub reproduktora
              </h1>
              {/* Mobile-only krótszy tytuł, większa kompresja pionowa */}
              <h1 className="sm:hidden text-3xl font-black tracking-tight mb-3" data-testid="katalog-title-mobile">
                Znajdź swojego psa
              </h1>
              <p className="hidden sm:block text-gray-400 text-base md:text-lg max-w-2xl mx-auto">
                Sprawdź dostępne mioty, hodowle i reproduktory w&nbsp;całej Polsce. Wyszukuj według rasy, miasta, województwa i&nbsp;organizacji.
              </p>
              {stats && (
                <p className="text-gray-600 text-xs mt-3" data-testid="katalog-stats">
                  {stats.kennels} hodowli · {stats.litters} miotów · {stats.studs} reproduktorów · {stats.breeds} ras
                </p>
              )}
            </div>
            <SearchBar
              initial={{ type: type === 'all' ? 'szczenieta' : type, breed: rasa, city: miasto, voivodeship: wojewodztwo }}
              onSearch={handleSearch}
            />
          </div>
        </section>

        {/* AI KONCIERŻ — opisz czego szukasz, dobierzemy oferty */}
        <section className="pb-2" data-testid="concierge-section">
          <div className="max-w-6xl mx-auto px-4 md:px-6">
            <CatalogConcierge lockMode="szczeniaki" />
          </div>
        </section>

        {/* POPULAR TAGS — zwijane aby oszczędzić miejsce (szczególnie mobile) */}
        {(popularBreeds.length > 0 || popularCities.length > 0) && (
          <section className="pb-4" data-testid="popular-tags">
            <div className="max-w-6xl mx-auto px-4 md:px-6">
              <details className="group bg-white/[0.02] border border-white/5 rounded-xl" data-testid="popular-tags-collapsible">
                <summary className="flex items-center justify-between px-4 py-2.5 cursor-pointer list-none select-none hover:bg-white/5 rounded-xl transition">
                  <span className="text-[11px] uppercase tracking-[0.18em] text-white/50 flex items-center gap-2">
                    <Flame className="w-3 h-3 text-orange-400" />
                    Popularne filtry
                    <span className="normal-case tracking-normal text-white/30 text-[10px]">
                      ({popularBreeds.length + popularCities.length})
                    </span>
                  </span>
                  <ChevronDown className="w-4 h-4 text-white/40 transition-transform group-open:rotate-180" />
                </summary>
                <div className="px-4 pb-4 pt-2 space-y-3">
                  {popularBreeds.length > 0 && (
                    <div className="flex flex-wrap items-center gap-2" data-testid="popular-breeds">
                      <span className="text-[10px] uppercase tracking-[0.18em] text-white/40 mr-1">Rasy:</span>
                      {popularBreeds.map(b => {
                        const isActive = (rasa || '').toLowerCase() === b.name.toLowerCase();
                        return (
                          <button
                            key={b.slug}
                            onClick={() => updateParams(isActive ? { rasa: null } : { rasa: b.name })}
                            data-testid={`popular-breed-${b.slug}`}
                            aria-pressed={isActive}
                            className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs transition border ${
                              isActive
                                ? 'bg-blue-500/30 border-blue-300 text-white shadow-sm shadow-blue-500/40'
                                : 'bg-blue-500/10 border-blue-500/25 text-blue-200 hover:bg-blue-500/20 hover:border-blue-400/50'
                            }`}
                          >
                            <Baby className="w-3 h-3" />
                            {b.name}
                            {isActive && <X className="w-3 h-3 ml-0.5 opacity-70" />}
                          </button>
                        );
                      })}
                    </div>
                  )}
                  {popularCities.length > 0 && (
                    <div className="flex flex-wrap items-center gap-2" data-testid="popular-cities">
                      <span className="text-[10px] uppercase tracking-[0.18em] text-white/40 mr-1">Miasta:</span>
                      {popularCities.map(c => {
                        const isActive = (miasto || '').toLowerCase() === c.city.toLowerCase();
                        return (
                          <button
                            key={c.city}
                            onClick={() => updateParams(isActive ? { miasto: null } : { miasto: c.city })}
                            data-testid={`popular-city-${c.city}`}
                            aria-pressed={isActive}
                            className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs transition border ${
                              isActive
                                ? 'bg-emerald-500/30 border-emerald-300 text-white shadow-sm shadow-emerald-500/40'
                                : 'bg-emerald-500/10 border-emerald-500/25 text-emerald-200 hover:bg-emerald-500/20 hover:border-emerald-400/50'
                            }`}
                          >
                            <MapPin className="w-3 h-3" />
                            {c.city}
                            {c.count > 0 && !isActive && <span className="text-[10px] text-emerald-300/60">· {c.count}</span>}
                            {isActive && <X className="w-3 h-3 ml-0.5 opacity-70" />}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </details>
            </div>
          </section>
        )}

        {/* ACTIVE FILTERS CHIPS */}
        {(rasa || miasto || wojewodztwo) && (
          <section className="pb-2">
            <div className="max-w-6xl mx-auto px-4 md:px-6">
              <div className="flex flex-wrap items-center gap-2" data-testid="active-filters-chips">
                <span className="text-xs text-gray-500 mr-1">Aktywne filtry:</span>
                {rasa && (
                  <button onClick={() => updateParams({ rasa: null })} data-testid="chip-rasa" className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-500/15 border border-blue-500/30 text-blue-300 text-xs hover:bg-blue-500/25">
                    Rasa: {rasa} <X className="w-3 h-3" />
                  </button>
                )}
                {miasto && (
                  <button onClick={() => updateParams({ miasto: null })} data-testid="chip-miasto" className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 text-xs hover:bg-emerald-500/25">
                    <MapPin className="w-3 h-3" /> {miasto} <X className="w-3 h-3" />
                  </button>
                )}
                {wojewodztwo && (
                  <button onClick={() => updateParams({ wojewodztwo: null })} data-testid="chip-wojewodztwo" className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 text-xs hover:bg-emerald-500/25">
                    Woj.: {wojewodztwo} <X className="w-3 h-3" />
                  </button>
                )}
                <button
                  onClick={() => setSearchParams({})}
                  data-testid="chip-clear-all"
                  className="text-xs text-gray-500 hover:text-white underline underline-offset-2 ml-2"
                >
                  Wyczyść
                </button>
              </div>
            </div>
          </section>
        )}

        {/* TOOLBAR: counts + sort + save search */}
        <section className="pb-3">
          <div className="max-w-6xl mx-auto px-4 md:px-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 border-b border-white/5 pb-3">
              <div className="text-sm text-white/60" data-testid="catalog-counts">
                {displayTotal} {displayTotal === 1 ? 'wynik' : displayTotal < 5 ? 'wyniki' : 'wyników'} · {TYPE_LABELS[normalizedType] || 'Wszystkie'}
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <SortDropdown value={sort} options={sortOptions} onChange={setSort} />
                <Button
                  onClick={() => setSaveModalOpen(true)}
                  size="sm"
                  variant="outline"
                  className="rounded-xl border-orange-500/40 bg-orange-500/10 text-orange-300 hover:bg-orange-500/20 text-sm font-semibold h-9 px-4 whitespace-nowrap"
                  data-testid="btn-save-search"
                >
                  <Bell className="w-3.5 h-3.5 mr-1.5" />
                  Zapisz wyszukiwanie
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* RESULTS */}
        <section className="pb-16">
          <div className="max-w-6xl mx-auto px-6">
            {loading && accumulated.length === 0 ? (
              <div className="py-20 flex flex-col items-center gap-3" data-testid="catalog-loading">
                <Loader2 className="w-8 h-8 animate-spin text-gray-500" />
                <p className="text-gray-500 text-sm">Ładujemy ogłoszenia…</p>
              </div>
            ) : (
              <>
                {/* SponsoredStrip — pokazuje hodowle/mioty/reproduktory oznaczone jako sponsored */}
                <SponsoredStrip sponsored={data?.sponsored} />

                {/* RECENTLY VIEWED — retencja, persistuje między wizytami */}
                {recent.length > 0 && (
                  <section className="mb-6 pb-5 border-b border-white/5" data-testid="recently-viewed">
                    <div className="flex items-center justify-between mb-3">
                      <h2 className="text-sm font-semibold text-white">Kontynuuj przeglądanie</h2>
                      <button
                        onClick={clearRecent}
                        className="text-[11px] text-white/40 hover:text-white/70 transition"
                        data-testid="recently-viewed-clear"
                      >
                        Wyczyść
                      </button>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                      {recent.map(r => (
                        <Link
                          key={r.id}
                          to={r.url || '#'}
                          data-testid={`recently-viewed-${r.slug}`}
                          className="group rounded-xl bg-white/5 border border-white/10 hover:border-orange-400/40 overflow-hidden transition"
                        >
                          <div className="relative aspect-[4/3] bg-[#080f1f] overflow-hidden">
                            {r.image ? (
                              <img src={getImageUrl(r.image)} alt={r.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                            ) : (
                              <KennelAvatar name={r.title} seed={r.slug || r.id} type={r.type} />
                            )}
                          </div>
                          <div className="p-2">
                            <div className="text-[12px] font-semibold text-white line-clamp-2 leading-tight">{r.title}</div>
                            {r.subtitle && <div className="text-[10px] text-white/45 mt-0.5 truncate">{r.subtitle}</div>}
                          </div>
                        </Link>
                      ))}
                    </div>
                  </section>
                )}

                {accumulated.length === 0 && !loading ? (
                  <div className="py-20 text-center" data-testid="catalog-empty-state">
                    <Search className="w-10 h-10 text-gray-600 mx-auto mb-4" />
                    <h2 className="text-2xl font-bold text-white mb-2">Nie znaleźliśmy pasujących ogłoszeń</h2>
                    <p className="text-gray-400 max-w-md mx-auto mb-6">
                      Spróbuj zmienić lokalizację<br />
                      albo ustaw powiadomienie — damy znać, gdy pojawi się oferta.
                    </p>
                    <div className="flex flex-wrap items-center justify-center gap-3">
                      <Button
                        onClick={() => setSaveModalOpen(true)}
                        className="rounded-full bg-orange-500 text-white hover:bg-orange-600 font-semibold px-5 h-11"
                        data-testid="empty-state-set-alert"
                      >
                        <Bell className="w-4 h-4 mr-1.5" />
                        Obserwuj to wyszukiwanie
                      </Button>
                      <button
                        onClick={() => setSearchParams({})}
                        className="px-5 py-2.5 rounded-full bg-white/5 border border-white/10 text-white hover:bg-white/10 text-sm font-semibold"
                        data-testid="catalog-reset-filters"
                      >
                        Wyczyść
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="mb-4 flex items-center justify-between">
                      <p className="text-sm text-gray-400" data-testid="catalog-results-count">
                        <span className="text-white font-semibold">{displayTotal}</span> {displayTotal === 1 ? 'wynik' : displayTotal < 5 ? 'wyniki' : 'wyników'}
                      </p>
                    </div>
                    <div className="space-y-4">
                      {accumulated.map(item => (
                        <ResultCard
                          key={`${item.type}-${item.slug || item.id}`}
                          item={item}
                          isFavorite={favorites.has(item.id)}
                          onFavoriteToggle={handleFavoriteToggle}
                          onLeadClick={(it) => setLeadModalItem(it)}
                          onView={addRecent}
                        />
                      ))}
                    </div>

                    {/* „Pokaż więcej" — infinite load zamiast paginacji */}
                    {accumulated.length < total && (
                      <div className="mt-10 flex flex-col items-center gap-2" data-testid="catalog-load-more-wrap">
                        <button
                          onClick={() => updateParams({ page: page + 1 })}
                          disabled={loading}
                          data-testid="catalog-load-more"
                          className="px-6 py-3 rounded-full bg-white/[0.06] border border-white/10 text-sm text-white font-semibold hover:bg-white/10 hover:border-white/20 disabled:opacity-40 inline-flex items-center gap-2 transition"
                        >
                          {loading ? (
                            <><Loader2 className="w-4 h-4 animate-spin" /> Ładujemy ogłoszenia…</>
                          ) : (
                            <>Pokaż więcej ogłoszeń</>
                          )}
                        </button>
                        <span className="text-xs text-white/40">
                          Pokazano {accumulated.length + sponsoredCount} z {displayTotal}
                        </span>
                      </div>
                    )}
                    {accumulated.length >= total && total > 15 && (
                      <div className="mt-10 text-center text-sm text-white/40" data-testid="catalog-all-loaded">
                        To wszystkie ogłoszenia pasujące do Twojego wyszukiwania.
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </section>

        <MarketplaceFooter />

        {/* Save Search Modal */}
        <SaveSearchModal
          open={saveModalOpen}
          onClose={() => setSaveModalOpen(false)}
          criteria={{ type: normalizedType, rasa, miasto, wojewodztwo, rodowod, status: status || null }}
          onSaved={() => {
            // Retencja: po zapisie wyszukiwania pokaż delikatny prompt logowania
            if (shouldShowLoginPrompt()) {
              setTimeout(() => setLoginPromptCtx('save_search'), 600);
            }
          }}
        />

        {/* Quick Lead Modal — v2 P0 */}
        <QuickLeadModal
          open={!!leadModalItem}
          onClose={() => setLeadModalItem(null)}
          item={leadModalItem}
        />

        {/* Login Prompt — retencja anonimowych userów */}
        <LoginPromptModal
          open={!!loginPromptCtx}
          context={loginPromptCtx || 'favorite'}
          onClose={() => setLoginPromptCtx(null)}
        />
      </div>
    </>
  );
}
